import { useWindowSize } from '@solinkcorp/solink-components';
import PlayerCameraItem from 'components/PlayerCameraItem/PlayerCameraItem';
import { type DownloadOption } from 'components/PublicPages/types/camera.types';
import React from 'react';
import useDimensions from 'react-cool-dimensions';
import { FixedSizeList, type ListChildComponentProps } from 'react-window';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type ThumbnailType } from 'vd-constants';
import { type PlayingCamerasController } from '../controller/PlayingCamerasController';
import styles from './PlayerCameraList.module.sass';

interface PlayerCameraListProps {
  cameras: CallhomeCamera[];
  playingCamerasController: PlayingCamerasController;
  cameraThumbnailType?: ThumbnailType;
  multiPlayDisabled?: boolean;
  playCamera: (camera: CallhomeCamera) => void;
  onUpdatePlayerCamera?: (id: string) => void;
  downloadOptionsByCameraId?: {
    [key: string]: DownloadOption;
  } | null;
  layout?: 'horizontal' | 'vertical';
}

const PlayerCameraList: React.FC<PlayerCameraListProps> = (props) => {
  const {
    cameras,
    playingCamerasController,
    cameraThumbnailType,
    multiPlayDisabled,
    playCamera,
    onUpdatePlayerCamera,
    downloadOptionsByCameraId,
    layout = 'vertical',
  } = props;

  const { height, width, observe: playerCameraListDimensions } = useDimensions();
  const isVertical = layout === 'vertical';
  const { width: windowWidth } = useWindowSize();

  const isMobile = windowWidth < 480;

  // Vertical Layout
  const verticalPaddingRight = 25;
  const verticalItemHeight = 145;
  const verticalItemPadding = 14;

  // Horizontal Layout
  const horizontalItemWidth = isMobile ? 150 : 250;
  const horizontalItemPadding = 10; // Space between horizontal items
  const horizontalItemHeight = (9 / 16) * horizontalItemWidth; // Aspect ratio

  const containerHeight = isVertical ? (height ?? 0) : horizontalItemHeight + 28;

  return (
    <div ref={playerCameraListDimensions} style={{ width: '100%', height: '100%' }}>
      <FixedSizeList
        height={containerHeight}
        width={width ?? 0}
        itemSize={
          isVertical
            ? verticalItemHeight + verticalItemPadding
            : horizontalItemWidth + horizontalItemPadding
        }
        layout={layout}
        style={{ paddingRight: isVertical ? `${verticalPaddingRight}px` : undefined }}
        className={styles['player-camera-list']}
        itemCount={cameras.length}
        itemData={{
          cameras,
          downloadOptionsByCameraId,
          playingCamerasController,
          cameraThumbnailType,
          multiPlayDisabled,
          playCamera,
          onUpdatePlayerCamera,
          layout,
          horizontalItemPadding,
          verticalItemPadding,
          verticalPaddingRight,
        }}
      >
        {ListItem}
      </FixedSizeList>
    </div>
  );
};

const ListItem = ({
  data,
  index,
  style,
}: ListChildComponentProps<
  PlayerCameraListProps & {
    horizontalItemPadding: number;
    verticalItemPadding: number;
    verticalPaddingRight: number;
  }
>) => {
  const camera = data.cameras[index];
  const downloadOption = data.downloadOptionsByCameraId?.[camera.id];

  return (
    <div
      style={{
        ...style,
        paddingBottom: data.layout === 'vertical' ? `${data.verticalItemPadding}px` : undefined,
        paddingRight: data.layout === 'horizontal' ? `${data.horizontalItemPadding}px` : undefined,
        width:
          data.layout === 'vertical' ? `calc(100% - ${data.verticalPaddingRight}px)` : style.width,
        height: 'auto',
      }}
    >
      <PlayerCameraItem
        key={`player-camera-item-${camera.id}`}
        camera={camera}
        downloadOption={downloadOption}
        playingCamerasController={data.playingCamerasController}
        cameraThumbnailType={data.cameraThumbnailType}
        multiPlayDisabled={data.multiPlayDisabled}
        playCamera={data.playCamera}
        onUpdatePlayerCamera={data.onUpdatePlayerCamera}
      />
    </div>
  );
};

export default React.memo(PlayerCameraList);
